import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Button, Dialog } from "@repo/ui";

export type CibleSuppression =
  | { kind: "campagne"; id: Id<"campagnes"> }
  | { kind: "parcours"; id: Id<"parcoursEvaluations"> };

const TITRES = {
  campagne: "Supprimer la campagne",
  parcours: "Supprimer le parcours",
} as const;

/**
 * Confirmation de suppression logique, avec l'aperçu de ce qui part. Monté seulement quand `cible` est définie : l'aperçu lit
 * toute la campagne, il ne doit pas rester abonné en arrière-plan.
 */
export function ConfirmerSuppression({
  cible,
  libelle,
  onClose,
  onSupprime,
}: {
  cible: CibleSuppression | null;
  libelle: string;
  onClose: () => void;
  onSupprime?: () => void;
}) {
  return (
    <Dialog isOpen={cible !== null} onClose={onClose} title={cible ? TITRES[cible.kind] : ""}>
      {cible && (
        <Contenu cible={cible} libelle={libelle} onClose={onClose} onSupprime={onSupprime} />
      )}
    </Dialog>
  );
}

function Contenu({
  cible,
  libelle,
  onClose,
  onSupprime,
}: {
  cible: CibleSuppression;
  libelle: string;
  onClose: () => void;
  onSupprime?: () => void;
}) {
  const apercu = useQuery(api.corbeille.apercuSuppression, { cible });
  const supprimer = useMutation(api.corbeille.supprimer);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmer = async () => {
    setError(null);
    setSaving(true);
    try {
      await supprimer({ cible });
      onClose();
      onSupprime?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      <p>
        <strong>{libelle}</strong> disparaîtra du portail client et du portail
        admin. Les liens candidat et évaluateur ne s'ouvriront plus.
      </p>
      {apercu === undefined ? (
        <p className="text-muted-foreground">Calcul de l'impact…</p>
      ) : (
        <ul className="list-disc pl-5 space-y-1">
          {cible.kind === "campagne" && <li>{apercu.nbParcours} parcours candidat(s)</li>}
          <li>
            {apercu.nbEvaluations} évaluation(s), dont {apercu.nbEvaluationsDemarrees} démarrée(s)
          </li>
          <li>Aucun crédit rendu : les évaluations supprimées restent dues</li>
        </ul>
      )}
      <p className="text-muted-foreground">
        Restaurable depuis la corbeille de l'organisation.
      </p>
      {error && (
        <p className="text-destructive bg-destructive/10 rounded-controle px-3 py-2">{error}</p>
      )}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button variant="destructive" disabled={saving || apercu === undefined} onClick={() => void confirmer()}>
          {saving ? "Suppression…" : "Supprimer"}
        </Button>
      </div>
    </div>
  );
}
